import { useRef, useState } from "react";
import { Spinner } from "@heroui/react";
import { uploadImage } from "../services/imageService";

interface PhotoPickerProps {
  type: "recipe" | "ingredient";
  imageUrl?: string | null;
  name?: string;
  /** Tint de fons quan no hi ha foto (classe CSS mt-<meal>). */
  meal?: string;
  onChange: (url: string | null) => void;
  /** Mida del quadrat en px. Per defecte 120. */
  size?: number;
  /** Ocupa tot l'ample del contenidor (proporció 4:3) en lloc del quadrat fix. */
  fill?: boolean;
}

function CameraIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function PhotoPicker({ type, imageUrl, name, meal = "lunch", onChange, size = 120, fill = false }: PhotoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imgOk, setImgOk] = useState(true);

  const hasImage = Boolean(imageUrl) && imgOk;

  async function handleFile(file: File) {
    if (!file.type.startsWith("image/")) { setError("Només imatges"); return; }
    if (file.size > 10 * 1024 * 1024) { setError("La foto no pot superar els 10 MB"); return; }
    setUploading(true);
    setError(null);
    try {
      const result = await uploadImage(file, type);
      if (inputRef.current) inputRef.current.value = "";
      onChange(result.path);
      setImgOk(true);
    } catch (err: any) {
      setError(err.message || "Error pujant la imatge");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-1" style={fill ? { width: "100%" } : { width: size }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />

      <div className="pp-media" style={fill ? { position: "relative", width: "100%", aspectRatio: "4 / 3" } : { position: "relative", width: size, height: size }}>
        {/* Zona clicable principal */}
        <button
          type="button"
          onClick={() => !uploading && inputRef.current?.click()}
          title={hasImage ? "Canvia la foto" : "Afegeix una foto"}
          style={{ width: "100%", height: "100%", padding: 0, border: "none", cursor: uploading ? "wait" : "pointer" }}
          className={`thumb mt-${meal} ${fill ? "" : "rounded-card-sm "}overflow-hidden flex items-center justify-center transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent${!hasImage ? " photo-picker-empty" : ""}`}
        >
          {hasImage ? (
            <img
              src={imageUrl!}
              alt={name ?? ""}
              className="w-full h-full object-cover"
              onError={() => setImgOk(false)}
            />
          ) : (
            <span className="flex flex-col items-center gap-1 opacity-60">
              <CameraIcon />
              <span style={{ fontSize: 11 }}>Foto</span>
            </span>
          )}

          {uploading && (
            <span
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: "rgba(0,0,0,0.35)" }}
            >
              <Spinner size="sm" color="current" />
            </span>
          )}
        </button>

        {/* Paperera — només quan hi ha imatge */}
        {hasImage && !uploading && (
          <button
            type="button"
            onClick={() => { onChange(null); setImgOk(true); }}
            title="Elimina la foto"
            className="rnd pp-del absolute top-1.5 right-1.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-white"
            aria-label="Elimina la foto"
          >
            <TrashIcon />
          </button>
        )}
      </div>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
